import {IconDeviceMobile} from "@tabler/icons-react";
import PropTypes from "prop-types";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import imgPayku from "../../assets/images/logoPayku.webp";
import imgBinance from "../../assets/images/modal-icons/binance-exchange.svg";
import imgBinanceDark from "../../assets/images/modal-icons/binance-exchange_darkmode.svg";
import imgZelle from "../../assets/images/modal-icons/zelle.svg";
import imgZelleDark from "../../assets/images/modal-icons/zelle_darkmode.svg";
import usePagoMovilRegistration from "../../hooks/usePagoMovilRegistration";
import {useBalanceStore} from "../../store/useBalance.store";
import useCurrencyStore from "../../store/useCurrency.store";
import {PrimaryButton} from "../Button";
import BankOptions from "./BankOptions";
import BinanceOptions from "./BinanceOptions";
import styles from "./DepositMethods.module.css";
import PaykuOptions from "./PaykuOptions";
import ZelleOptions from "./ZelleOptions";

const DepositMethods = ({onWidthChange}) => {
  const [showBankOptions, setShowBankOptions] = useState(false);
  const [showZelleOptions, setShowZelleOptions] = useState(false);
  const [showBackButton, setShowBackButton] = useState(false);
  const [showBinanceOptions, setShowBinanceOptions] = useState(false);
  const [showPaykuOptions, setShowPaykuOptions] = useState(false);
  const [isDarkTheme, setIsDarkTheme] = useState(() =>
    typeof document !== "undefined" ? document.documentElement.getAttribute("data-theme") === "dark" : false
  );

  const handleBankAccountModal = useBalanceStore(state => state.handleBankAccountModal);
  const {triggerRegistrationModalIfNeeded} = usePagoMovilRegistration();
  const {selectedCurrency} = useCurrencyStore();

  const {t} = useTranslation();

  // Set modal width dynamically based on currency (larger for CLP to fit 4 options)
  useEffect(() => {
    if (onWidthChange) {
      onWidthChange("678px");
    }
  }, [onWidthChange, selectedCurrency]);

  useEffect(() => {
    const root = document.documentElement;
    const syncTheme = () => {
      setIsDarkTheme(root.getAttribute("data-theme") === "dark");
    };

    syncTheme();
    const observer = new MutationObserver(syncTheme);
    observer.observe(root, {attributes: true, attributeFilter: ["data-theme"]});

    return () => observer.disconnect();
  }, []);

  const prev = () => {
    setShowBankOptions(false);
    setShowZelleOptions(false);
    setShowBackButton(false);
    setShowBinanceOptions(false);
    setShowPaykuOptions(false);
    if (onWidthChange) {
      onWidthChange("678px");
    }
  };

  const handleBankClick = async () => {
    // Check if user needs to register cedula and phone number
    // Use forceRefresh to ensure we have the latest user data
    const modalTriggered = await triggerRegistrationModalIfNeeded("deposit", true);

    // Only proceed if registration is not needed
    if (!modalTriggered) {
      setShowBankOptions(true);
      setShowZelleOptions(false);
      setShowBackButton(true);
      if (onWidthChange) {
        onWidthChange("893px");
      }
    }
  };

  const handleZelleClick = () => {
    setShowZelleOptions(true);
    setShowBankOptions(false);
    setShowBackButton(true);
    if (onWidthChange) {
      onWidthChange("678px");
    }
  };

  const handleBinanceClick = () => {
    setShowBinanceOptions(true);
    setShowBankOptions(false);
    setShowZelleOptions(false);
    setShowPaykuOptions(false);
    setShowBackButton(true);
    if (onWidthChange) {
      onWidthChange("678px");
    }
  };

  const handlePaykuClick = () => {
    setShowPaykuOptions(true);
    setShowBankOptions(false);
    setShowZelleOptions(false);
    setShowBinanceOptions(false);
    setShowBackButton(true);
    if (onWidthChange) {
      onWidthChange("678px");
    }
  };

  // const handleCardsClick = () => {
  //   setShowCardsOptions(true);
  //   setShowZelleOptions(false);
  //   setShowBackButton(true);
  // };

  return (
    <div className={styles.depositMethodsModalContainer}>
      {!showBankOptions && !showZelleOptions && !showBinanceOptions && !showPaykuOptions && (
        <>
          <h4 className={styles.deposit_option_instructions}>{t("depositInstructionsOptions")}</h4>

          <div className={styles.deposit_option_logo_container}>
            {selectedCurrency?.id !== "CLP" && (
              <div
                className={styles.methodCard}
                onClick={handleBankClick}
              >
                <IconDeviceMobile
                  size={24}
                  color="var(--text-primary)"
                />
                <span className={styles.methodLabel}>Pago Móvil</span>
              </div>
            )}
            <div
              className={styles.methodCard}
              onClick={handleZelleClick}
            >
              <img
                src={isDarkTheme ? imgZelleDark : imgZelle}
                alt="Zelle"
                className={`${styles.methodLogo} ${styles.zelleLogo}`}
              />
            </div>
            <div
              className={styles.methodCard}
              onClick={handleBinanceClick}
            >
              <img
                src={isDarkTheme ? imgBinanceDark : imgBinance}
                alt="Binance"
                className={`${styles.methodLogo} ${styles.binanceLogo}`}
              />
            </div>
            {selectedCurrency?.id === "CLP" && (
              <div
                className={styles.methodCard}
                onClick={handlePaykuClick}
              >
                <img
                  src={imgPayku}
                  alt="Payku"
                  className={styles.methodLogo}
                />
              </div>
            )}
          </div>
        </>
      )}

      {showBankOptions && (
        <>
          <h4 className={styles.deposit_option_instructions}>Pago Móvil</h4>
          <BankOptions prev={prev} />
        </>
      )}
      {showZelleOptions && <ZelleOptions prev={prev} />}
      {showBinanceOptions && <BinanceOptions prev={prev} />}
      {showPaykuOptions && <PaykuOptions onBack={prev} />}

      {!showBackButton && (
        <div className={styles.cancelRow}>
          <PrimaryButton onClick={handleBankAccountModal}>{t("depositInstructionsCancelBtn")}</PrimaryButton>
        </div>
      )}
    </div>
  );
};
DepositMethods.propTypes = {
  onWidthChange: PropTypes.func,
};

export default DepositMethods;
